import React from 'react';
import { useMetricType } from 'hooks/useMetricType';
import { Checkbox, IconButton, Tooltip, Typography } from '@mui/material';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import { CONVERSIONS, getMetricUnit } from 'util/conversions';
import {
  PsdActionCell,
  PsdCheckboxCell,
  PsdGridRow,
  PsdPercentCell,
  PsdSizeCell,
  PsdSpanValuesCell,
} from 'containers/PropertyPanel/Cards/Feed/PsdGrid';
import { ReactComponent as TrashcanIcon } from 'icons/trashcan.svg';

interface DeleteCheckboxProps {
  removeIndexes: number[];
  setRemoveIndexes(newRemoveIndexes: number[]): void;
  newSizeList: number[];
}

interface PsdTableHeadInterface extends DeleteCheckboxProps {
  editing: boolean;
  remove(): void;
  addNewRow: boolean;
  toggleAddNewRow(): void;
  hidePsd: boolean;
}

const DeleteCheckbox = ({
  newSizeList,
  removeIndexes,
  setRemoveIndexes,
}: DeleteCheckboxProps) => (
  <Checkbox
    size='small'
    color='info'
    indeterminate={
      removeIndexes.length > 0 && removeIndexes.length !== newSizeList.length
    }
    checked={removeIndexes.length > 0}
    onChange={() => {
      if (removeIndexes.length > 0) {
        setRemoveIndexes([]);
      } else {
        const allIndexes = newSizeList.map((s, index) => index);
        setRemoveIndexes(allIndexes);
      }
    }}
  />
);

export const PsdTableHead = ({
  removeIndexes,
  setRemoveIndexes,
  editing,
  newSizeList,
  remove,
  addNewRow,
  toggleAddNewRow,
  hidePsd,
}: PsdTableHeadInterface) => {
  const metricType = useMetricType();
  const isDeleteMode = removeIndexes.length > 0;
  const disableAddButton = addNewRow || editing;

  return (
    <PsdGridRow>
      <PsdCheckboxCell>
        <DeleteCheckbox
          newSizeList={newSizeList}
          removeIndexes={removeIndexes}
          setRemoveIndexes={setRemoveIndexes}
        />
      </PsdCheckboxCell>

      {!isDeleteMode ? (
        <>
          <PsdSizeCell>
            <Typography variant={'tableHeader'}>{`Size [${getMetricUnit(
              metricType,
              CONVERSIONS.MILLIMETER,
            )}]`}</Typography>
          </PsdSizeCell>
          {!hidePsd && (
            <PsdPercentCell>
              <Typography variant={'tableHeader'}>{'Percent [%]'}</Typography>
            </PsdPercentCell>
          )}
        </>
      ) : (
        <PsdSpanValuesCell>
          <Typography
            variant={'body2'}
          >{`${removeIndexes.length} selected`}</Typography>
        </PsdSpanValuesCell>
      )}

      <PsdActionCell>
        {isDeleteMode ? (
          <IconButton size='small' onClick={remove}>
            <Tooltip title={'Delete selected rows'}>
              <TrashcanIcon color={'warning'} />
            </Tooltip>
          </IconButton>
        ) : (
          <IconButton
            size='small'
            disabled={disableAddButton}
            onClick={toggleAddNewRow}
          >
            <Tooltip title={'Add new row'}>
              <AddCircleIcon
                color={disableAddButton ? 'disabled' : 'primary'}
              />
            </Tooltip>
          </IconButton>
        )}
      </PsdActionCell>
    </PsdGridRow>
  );
};
